<template>
  <div :class="{'free-user': freeUser, 'has-pro-features': hasProFeatures.length}" class="modal-frame">
    <!-- <h3 class="title is-3">{{ slideDeck.name }}</h3> -->
    <div class="code-and-players">
      <div class="event-code">
        <p class="label">Event Code</p>
        <b-input
          v-model="userShortcode"
          :disabled="freeUser"
          :class="{'is-invalid': isCodeValid === false}"
          size="is-large"
          maxlength="10"
          minlength="3"
          expanded
          data-cy="start-modal-event-code-input"
        />
        <button
          @click="changeShortcode($event)"
          class="button is-dark change-code"
          type="is-dark"
        >
          Change &nbsp;<i class="fas fa-sync" />
        </button>
        <p v-if="freeUser" @click="startUpgrade()" class="upgrade-text">✨ Upgrade to enter your own custom event code</p>
        <p v-else :class="{'is-invalid': isCodeValid === false}" class="upgrade-text pro">{{ isCodeValid ? 'Enter your custom event code above' : validationMessage }}</p>
      </div>
      <div class="sound-and-limit">
        <template v-if="freeUser">
          <b-tooltip
            size="is-small"
            multilined
            label="Upgrade to increase player limit to up to 1000"
          >
            <div @click="startUpgrade()" class="player-limit">
              <p class="label m-t-20">Player Limit</p>
              <p class="title is-4">{{ playerLimit }} <i class="fas fa-user-friends" /></p>
            </div>
          </b-tooltip>
        </template>
        <template v-else>
          <div class="player-limit">
            <p class="label m-t-20">Player Limit</p>
            <p class="title is-4">{{ playerLimit }} <i class="fas fa-user-friends" /></p>
          </div>
        </template>

        <div class="sound-settings">
          <p class="label m-t-20">Soundboard</p>
          <p v-if="!disableSounds" @click="toggleSounds()" class="title is-4">ON <i class="fas fa-volume-up" /></p>
          <p v-else @click="toggleSounds()" class="title is-4">OFF <i class="fas fa-volume-mute" /></p>
        </div>

        <div class="avatar-style">
          <p class="label m-t-20">Avatars</p>
          <!-- Do NOT change the `:append-to-body="false"` prop, or the Pro tooltips will show below the selector body -->
          <AvatarStyleSelector
            v-model="avatarStyle"
            @input="triggerSaveSettings({tracking: 'avatar_style_' + (avatarStyle || 'friendly')})"
            @upgrade="startUpgrade"
            :disable-pro-options="!isProUser"
            :append-to-body="false"
            :compact="true"
            upgrade-intent="use different player avatar and join-in styling"
          />
        </div>
      </div>
    </div>

    <div class="pro-options">
      <div class="toggle">
        &laquo; {{ freeUser ? 'premium' : 'advanced' }} options &raquo;
      </div>
      <div class="options">
        <b-checkbox v-model="disableRecap" @input="triggerSaveSettings({tracking: 'disable_recap_' + disableRecap })">
          <b-tooltip
            label="Hide your post-event recap from participants (will only be visible on your account)."
            position="is-right"
            type="is-dark"
            size="is-large"
            multilined
          >
            Disable event recap for players
            <!-- <b-icon pack="fas" icon="question-circle" /> -->
          </b-tooltip>
        </b-checkbox>

        <b-checkbox v-model="disableRatings" @input="triggerSaveSettings({tracking: 'disable_ratings_' + disableRatings})">
          <b-tooltip
            position="is-right"
            type="is-dark"
            size="is-large"
            multilined
          >
            <template v-slot:content>
              <p class="m-b-10">Remove the option to rate the event when it is ended. You’ll lose stats for this event.</p>
              <img src="/deck-event-rating.jpg" style="width: 100%; height: auto;">
            </template>
            Disable player event ratings
            <!-- <b-icon pack="fas" icon="question-circle" /> -->
          </b-tooltip>
        </b-checkbox>

        <b-tooltip
          label="Allow players to join as spectators and play along separately from the main event."
          position="is-right"
          type="is-dark"
          size="is-large"
          multilined
        >
          <b-checkbox v-if="canUseSpectatorEvent" v-model="useSpectatorEvent" :disabled="avatarStyle === 'none'" @input="triggerSaveSettings({tracking: 'use_spectator_event_' + useSpectatorEvent})">
            Allow players to join as spectators
            <!-- <b-icon pack="fas" icon="question-circle" /> -->
          </b-checkbox>
        </b-tooltip>

        <b-checkbox v-model="requirePassword" @input="triggerSaveSettings({tracking: 'require_password_' + requirePassword})">
          <b-tooltip
            label="Require players to enter a password to join the event. Useful for limiting public events to a specific group of participants."
            position="is-right"
            type="is-dark"
            size="is-large"
            multilined
          >
            Require password to join event
            <!-- <b-icon pack="fas" icon="question-circle" /> -->
          </b-tooltip>
        </b-checkbox>
        <b-field v-if="requirePassword" style="margin: 4px 26px 5px 35px;">
          <b-input
            ref="eventPassword"
            v-model="password"
            @input="triggerSaveSettings()"
            pattern="[a-zA-Z0-9]{3,30}"
            required
            validation-message="Must be at least 3 letters or numbers."
            size="is-small" type="text" placeholder="Enter password"
          />
        </b-field>
      </div>
      <div @click="startUpgrade()" class="upgrade-tip">
        Upgrade to change
      </div>
    </div>

    <div class="collect-emails">
      <b-tooltip :triggers="shouldDisplayEmailOnboarding ? [] : ['click', 'hover', 'focus']" class="large-tooltip is-wide" type="is-dark" position="is-right" multilined>
        <template v-slot:content v-if="avatarStyle === 'none'">
          Collecting emails is not available for anonymous avatar style.
        </template>
        <template v-slot:content v-else>
          <p><strong>Collecting emails enables:</strong></p>
          <ul class="list">
            <li>participants to rejoin your event if they lose the page</li>
            <li>you to send a recap to participants after event</li>
            <li>tracking participant performance over time (coming soon)</li>
            <li>export of participant list, with email, participation rate, and more (premium only)</li>
          </ul>
        </template>
        <b-field>
          <b-select v-model="collectPlayerEmails" @input="onInputCollectEmails" :disabled="avatarStyle === 'none'" class="is-brand-primary" expanded>
            <option value="required">Collect participant emails (email entry required to join)</option>
            <option value="optional">Request participant emails (email entry optional)</option>
            <option value="doNotCollect">Do not collect participant emails</option>
          </b-select>
        </b-field>
      </b-tooltip>

      <b-tooltip v-if="shouldDisplayEmailOnboarding" class="large-tooltip is-wide onboarding-tooltip" position="is-right" multilined always>
        <template v-slot:content>
          <div class="is-flex m-b-10">
            <div>
              <h6 class="title is-5 has-text-white">See what’s new</h6>
              <p class="subtitle is-6">You can now get email addresses from players</p>
            </div>
            <div @click="dismissCollectEmails" class="close-button">
              <i class="fas fa-times" />
            </div>
          </div>
          <img src="/collect-user-email-modal.jpg" style="width: 100%; height: auto;">
        </template>
      </b-tooltip>
    </div>

    <div class="buttons is-right">
      <button
        @click="tryStartLaunchEvent()"
        :title="validationMessage"
        :disabled="disableLaunchEvent"
        class="button is-fullwidth is-medium is-primary m-t-20"
        data-cy="modal-start-event-button"
      >
        Launch Event&nbsp; <strong style="text-transform: uppercase">{{ shortcode }}</strong>
      </button>
      <p v-if="validationMessage" class="small-validate">{{ validationMessage }}</p>
      <button v-else-if="allowSaveSettings" @click="triggerSaveSettings({shortcode}, $event)" class="button is-text is-fullwidth save-settings">Save these settings</button>
    </div>

    <div v-if="freeUser" class="upgrade-bar">
      <img class="callout" src="/upgrade-fishbowl.svg">
      <h3 class="title is-3 is-spaced">
        <template v-if="!hasProFeatures.length">Upgrade your events</template>
        <template v-else>Upgrade to use all of your selections</template>
      </h3>
      <p class="subtitle is-5">
        <template v-if="!hasProFeatures.length">
          Host up to 1000 people with custom event codes, data export, and even more ways to interact.
        </template>
        <template v-else>
          ☝️ You’ve <a @click="showDeckSettingsModal">selected</a> options that are premium-only. Your event won’t have these features.
          <ul>
            <li v-for="feature in hasProFeatures" :key="feature"><strong>{{ feature }}</strong></li>
            <li v-for="feature in remainingProFeatures" :key="feature">{{ feature }}</li>
          </ul>
        </template>
      </p>
      <button @click="startUpgrade()" class="button is-secondary">
        <template v-if="!hasProFeatures.length">Upgrade to Pro</template>
        <template v-else>Upgrade to unlock</template>
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import { isBefore } from 'date-fns'
import UpgradeAccount from '~/components/modals/UpgradeAccount'
import EventControls from '~/components/mixins/EventControls'
import AvatarStyleSelector from '~/components/decks/AvatarStyleSelector'
import DeckSettingsModalControls from '~/components/mixins/DeckSettingsModalControls'

/*
* Shortcodes and details around starting the event
* can be found in the EventControls mixin
*/

export default {
  name: 'StartEventModal',
  components: { AvatarStyleSelector },
  mixins: [EventControls, DeckSettingsModalControls],
  props: {
    slideDeck: {
      type: Object,
      required: true
    },
    allowSaveSettings: {
      type: Boolean,
      default: false
    },
    canEditDeck: {
      type: Boolean,
      default: false
    },
    canEditSettings: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      shortcode: '',
      avatarStyle: null,
      disableSounds: false,
      disableRecap: false,
      disableRatings: false,
      isStartingEvent: false,
      requirePassword: false,
      useSpectatorEvent: false,
      collectPlayerEmails: null,
      password: '',
      hasSkippedEmailOnboarding: false
    }
  },
  computed: {
    playerLimit () {
      return this.$store.getters.playerLimit
    },
    freeUser () {
      return this.$store.getters.userPlan === 'free'
    },
    canUseSpectatorEvent () {
      const allowedPlans = ['admin', 'pro500', 'pro1000']
      const userPlan = this.$store.getters.userPlan
      return allowedPlans.includes(userPlan)
    },
    isValidPassword () {
      return this.requirePassword === false || !!this.password.match(/[a-zA-Z0-9]{3,50}/)
    },
    userShortcode: {
      get () {
        return this.shortcode
      },
      set (val) {
        if (this.freeUser) { return }
        this.shortcode = val.toLowerCase()
        if (this.isCodeValid) {
          this.triggerSaveSettings({ shortcode: this.shortcode })
        }
      }
    },
    disableLaunchEvent () {
      return this.isCodeValid === false ||
             this.isStartingEvent === true ||
             this.isValidPassword === false ||
             (this.avatarStyle === 'none' && this.useSpectatorEvent === true)
    },
    isCodeValid () {
      const isReserved = this.isCodeReserved
      const isValid = /^[a-zA-Z0-9]{3,10}$/.test(this.shortcode)
      return isValid && !isReserved
    },
    isCodeReserved () {
      const reservedCodes = [
        'create',
        'join',
        'host',
        'event',
        'dashboard',
        'admin',
        'settings',
        'analytics',
        'edit',
        'preview',
        'recap',
        'test',
        'user',
        'player',
        'decks',
        'login',
        'signup',
        'account',
        'profile',
        'templates',
        'events',
        'spectate',
        'password',
        'register'
      ]

      return reservedCodes.includes(this.shortcode)
    },
    validationMessage () {
      if (!this.shortcode || this.shortcode.length < 3) {
        return 'Event code should be three or more characters'
      }

      if (this.isCodeReserved) { return 'Event code is already in use' }

      if (!this.isCodeValid) { return 'Event code can only use letters and numbers' }

      if (this.requirePassword && this.password?.length < 1) { return 'Please enter a password for this event' }

      if (!this.isValidPassword) { return 'Password must be at least 3 letters or numbers' }

      return ''
    },
    isProUser () {
      return this.$store.getters.isProUser
    },
    hasProFeatures () {
      if (!this.slideDeck || !this.canEditSettings) { return false }

      const { avatarStyle = null, disableRecap = false, disableRatings = false } = this.slideDeck.eventSettings || {}

      const proFeatures = []

      if (this.avatarStyle !== avatarStyle) {
        proFeatures.push('Choose any avatar style')
      }

      if (this.disableRecap !== disableRecap || this.disableRatings !== disableRatings) {
        proFeatures.push('Control your event reporting')
      }

      if (this.slideDeck.metaData.userLogoUrl) {
        proFeatures.push('Display your logo / remove ours')
      }

      return proFeatures
    },
    remainingProFeatures () {
      return [
        'Choose any avatar style',
        'Host more players',
        'Display your logo / remove ours',
        'Control your event reporting',
        'and more'
      ].filter(x => !this.hasProFeatures.includes(x))
    },
    isEduUser () {
      return this.$store.state.user?.isEdu
    },
    slideCreatedBeforeCollectEmailsRelease () {
      return isBefore(new Date(this.slideDeck.dateCreated), new Date('2025-08-07'))
    },
    shouldDisplayEmailOnboarding () {
      return (
        isBefore(new Date(this.$store.state.user.createdAt), new Date('2025-08-07')) &&
        !this.hasOnboarded('collect-player-emails')
      ) && !this.hasSkippedEmailOnboarding
    }
  },
  async created () {
    this.mergeSavedSettings()
    this.setCollectEmailsDefaults()
    await this.replaceShortcodeIfTaken()
  },
  destroyed () {
    this.dismissCollectEmails()
  },
  methods: {
    async replaceShortcodeIfTaken () {
      const savedShortcodeIsAvailable = this.shortcode && await this.isShortcodeAvailable(this.shortcode)
      if (savedShortcodeIsAvailable) { return }

      this.shortcode = await this.getShortcode()
    },
    async changeShortcode ($event) {
      $event.target.classList.add('is-loading')
      try {
        this.shortcode = await this.getShortcode()
        this.trackPosthog('event:launch_modal_setting_change', { setting: 'change_shortcode' })
      } finally {
        $event.target.classList.remove('is-loading')
      }
    },
    toggleSounds () {
      this.disableSounds = !this.disableSounds
      this.triggerSaveSettings({ tracking: 'disable_sounds_' + this.disableSounds }
      )
    },
    tryStartLaunchEvent (withHostControls = false) {
      this.log('trying to start event, isStartingEvent:', this.isStartingEvent)
      if (this.isStartingEvent) {
        this.log('already starting event, ignoring click')
      } else {
        this.log('starting event')
        this.isStartingEvent = true
        this.startLaunchEvent(withHostControls)
      }
    },
    async startLaunchEvent (withHostControls = false) {
      const settings = {
        avatarStyle: this.avatarStyle,
        disableSounds: this.disableSounds,
        disableRecap: this.disableRecap,
        disableRatings: this.disableRatings,
        requirePassword: this.requirePassword,
        useSpectatorEvent: this.useSpectatorEvent,
        collectPlayerEmails: this.collectPlayerEmails
      }

      if (this.requirePassword && this.password?.length >= 3) {
        settings.password = 'cGzc3loWe2aGsEf' + btoa(this.password + 'nacl')
        if (!this.useSpectatorEvent) { settings.hideJoinLink = true }
      }

      const isShortcodeAvailable = await this.isShortcodeAvailable(this.shortcode)

      if (!isShortcodeAvailable) {
        this.$buefy.toast.open('That shortcode is in use, try another one')
        return
      }

      this.triggerSaveSettings({ shortcode: this.shortcode })
      if (withHostControls) {
        const updatedSettings = { ...settings, stopRedirect: true }
        const event = await this.startEvent(this.slideDeck, updatedSettings)
        this.openHostControls(event.shortcode)
        this.$router.push('/event/' + event.shortcode)
      } else {
        this.startEvent(this.slideDeck, { settings })
      }

      this.posthogCapture('event:launch_modal_event_start', { shortcode: this.shortcode })

      setTimeout(() => {
        this.isStartingEvent = false
      }, 200)
      this.$emit('close')
    },
    startUpgrade () {
      this.$buefy.modal.open({
        parent: this,
        fullScreen: true,
        component: UpgradeAccount,
        canCancel: false,
        customClass: 'upgrade-modal',
        props: {
          from_location: 'start_event_modal',
          button_text: 'Upgrade to unlock'
        },
        trapFocus: true
      })

      // this.$emit('close')
    },
    mergeSavedSettings () {
      const settings = this.slideDeck?.eventSettings

      this.disableSounds = settings.disableSounds

      if (settings?.collectPlayerEmails) {
        this.collectPlayerEmails = settings.collectPlayerEmails
      }

      if (this.freeUser || this.canEditDeck !== true) { return }

      this.log('merging saved event settings from deck', settings)
      const useSpectatorEvent = this.canUseSpectatorEvent ? settings.useSpectatorEvent : false

      if (settings?.hasOwnProperty('disableSounds')) {
        this.avatarStyle = settings.avatarStyle
        this.disableRatings = settings.disableRatings
        this.disableRecap = settings.disableRecap
        this.useSpectatorEvent = useSpectatorEvent
        this.requirePassword = settings.requirePassword || false
        if (this.requirePassword === true && settings?.password?.length) {
          this.password = atob(settings.password.replace('cGzc3loWe2aGsEf', '')).replace('nacl', '')
        }
      }

      if (settings?.shortcode?.length >= 3) {
        this.shortcode = settings.shortcode
      }
    },
    triggerSaveSettings (options = {}, $event) {
      if (this.freeUser || !this.canEditDeck || !this.allowSaveSettings) { return }
      if ($event?.target) { $event.target.classList.add('is-loading') }

      if (this.avatarStyle === 'none' && this.useSpectatorEvent === true) {
        this.$buefy.toast.open({ message: 'Spectator events cannot be used with anonymous avatar style', type: 'is-danger' })
        this.useSpectatorEvent = false
        return
      }

      if (this.avatarStyle === 'none' && this.collectPlayerEmails !== 'doNotCollect') {
        this.$buefy.toast.open({ message: 'Collecting emails is not available for the anonymous avatar style, so we\'ve set it to "Do Not Collect"', type: 'is-info' })
        this.collectPlayerEmails = 'doNotCollect'
      }

      const settings = {
        avatarStyle: this.avatarStyle,
        disableSounds: this.disableSounds,
        disableRecap: this.disableRecap,
        disableRatings: this.disableRatings,
        requirePassword: this.requirePassword,
        useSpectatorEvent: this.useSpectatorEvent,
        password: 'cGzc3loWe2aGsEf' + btoa(this.password + 'nacl'),
        collectPlayerEmails: this.collectPlayerEmails
      }

      if (options.shortcode) { settings.shortcode = this.shortcode }

      this.log('trigger deck save event settings:', settings)
      this.$emit('update:settings', settings)

      if (options.tracking) {
        this.trackPosthog('event:launch_modal_setting_change', { setting: options.tracking })
      }

      if ($event?.target) {
        setTimeout(() => {
          $event.target.classList.remove('is-loading')
          this.$buefy.toast.open({ message: 'Settings saved', type: 'is-success' })
        }, 250)
      }
    },
    trackPosthog (event, addedProps) {
      const props = {
        deck_id: this.slideDeck.id,
        deck_name: this.slideDeck.name,
        ...addedProps
      }
      this.posthogCapture(event, props)
    },
    showDeckSettingsModal () {
      this.$emit('settings')
      this.$parent.close()
    },
    setCollectEmailsDefaults () {
      if (this.collectPlayerEmails) { return }
      if (this.avatarStyle === 'none') {
        this.collectPlayerEmails = 'doNotCollect'
        return
      }

      if (this.isEduUser || (this.isProUser && isBefore(new Date(this.slideDeck.dateCreated), new Date('2025-08-07')))) {
        this.collectPlayerEmails = 'doNotCollect'
        return
      } else if (this.freeUser) {
        this.collectPlayerEmails = 'required'
        return
      }

      this.collectPlayerEmails = 'optional'
    },
    dismissCollectEmails () {
      this.hasSkippedEmailOnboarding = true
      if (!this.hasOnboarded('collect-player-emails')) {
        this.saveOnboarding('collect-player-emails')
      }
    },
    onInputCollectEmails () {
      this.dismissCollectEmails()
      this.triggerSaveSettings({ collectPlayerEmails: this.collectPlayerEmails })
    },
    openHostControls (shortcode) {
      const url = process.env.SITE_URL + '/host/' + shortcode
      const windowFeatures = 'width=600,height=900,left=100,top=100,resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no,directories=no,copyhistory=no'
      const newWindow = window.open(url, 'hostControls', windowFeatures)
      // Focus the new window to make it pop out
      if (newWindow) {
        newWindow.focus()
      }
    }
  }
}
</script>

<style lang="scss">
.start-event-modal.modal {
  .modal-content {
    max-width: 500px !important;
    overflow: visible;
  }

  .event-code {
    position: relative;
    // width: 65%;

    input {
      text-transform: uppercase;
      padding-right: 130px;
    }

    .button.is-dark {
      // background-color: #666;
    }

    .change-code {
      position: absolute;
      right: 10px;
      bottom: 32px;
    }

    .control.is-invalid {
      input {
        background-color: rgba(255,0,0,.05);
        border-color: rgba(255,0,0,.2);
      }
    }
  }

  .small-validate {
    width: 100%;
    text-align: center;
    color: #da0b0b;
    font-size: .8em;
    margin-top: 5px;
    margin-bottom: -11px;
  }

  .pro-options {
    .help.is-danger {
      display: none;
    }
  }

  .code-and-players {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .pro-options {
    border: 1px solid #eee;
    background-color: #fafafa;
    border-radius: 4px;
    padding: 30px 10px 10px;
    margin: 20px 0 10px;
    position: relative;

    .upgrade-tip {
      display: none;
    }
  }

  .free-user .pro-options {
    position: relative;

    .toggle {
      z-index: 10;
    }

    .upgrade-tip {
      display: flex;
      opacity: 0;
      transition: opacity .2s ease;
      position:  absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      margin: 0 auto;
      width: 100%;
      height: 100%;
      background-color: rgba(255,255,255,.7);
      justify-content: center;
      align-items: center;
      font-weight: bold;
      cursor: pointer;
    }

    &:hover .upgrade-tip {
      opacity: 1;
    }

    .options {
      .checkbox {
        pointer-events: none;

        &:hover {
          // pointer-events: all;
        }

        &:active {
          pointer-events: none;
        }

        .tooltip-trigger {
          opacity: .6;
        }

        .check {
          opacity :.6;
          border-color: #7a7a7a;
        }
      }
    }
  }

  .collect-emails {
    .b-tooltip.is-multiline {
      &.is-dark .tooltip-content:before {
        border-right-color: #363633;
      }
      .tooltip-content {
        text-align: left;
        strong {
          color: white;
        }
        ul.list {
          list-style-type: disc;
          padding-left: 1rem;
        }
      }
    }
  }

  .b-tooltip.large-tooltip.is-multiline {
    width: 100%;

    &.onboarding-tooltip {
      .close-button {
        cursor: pointer;
      }

      .tooltip-content:before {
        transform: translateY(-50px);
      }
    }

    .control.is-brand-primary .select {
      &::after {
        border-color: var(--primary);
      }
    }
  }

  .notification.upgrade-notification {
    margin-top: 30px;
    color: rgb(74, 74, 74);
    background: #EEEEEE;
  }
}
</style>

<style lang="scss" scoped>
.modal-frame {
  background-color: #fff;
  border-radius: 6px;
  // overflow: hidden;
  padding: 45px 35px;
  transition: background-color .2s ease;

  &.is-recap {
    background-color: #f3f5f9;
  }

  &.free-user {
    margin-left: -200px;
    padding-right: 245px;

    &.has-pro-features {
      min-height: 680px;
    }
  }
}

.button.save-settings {
  // margin-bottom: -25px;
}

.button.launch-host-event {
  margin-bottom: -25px;
}

.upgrade-bar{
  background-color: #eee;
  position:  absolute;
  top: 0;
  padding: 50px 30px;
  text-align: center;
  bottom: 0;
  left: 290px;
  width: 300px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;

  img {
    width: 160px;
    margin-left: -5px;
  }

  a {
    color: var(--secondary);
    &:hover {
      text-decoration: underline;
    }
  }

  ul {
    margin: 30px 0 30px 20px;
    li {
      list-style: disc;
      text-align: left;
      font-size: .8em;
      margin-bottom: 5px;
    }
  }
}

.has-pro-features {
  .subtitle {
    text-align: left;
  }
  .upgrade-bar {
    width: 340px;
  }
}

.label {
  color: #888;
  font-weight: normal;
  text-transform: uppercase;
  font-size: .7em;
  margin: 0 0 2px 0;
}
.sound-and-limit {
  display: flex;
  gap: 40px;
  justify-content: space-around;
  margin: 40px 0 30px;
  // padding: 0 15px;
}

.sound-settings {
  .title {
    font-size: 2.2em;
    cursor: pointer;
    user-select: none;
  }
}

.avatar-style {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upgrade-text {
  position: absolute;
  bottom: -1px;
  left: 0px;
  font-size: .68em;
  color: #666;
  cursor: pointer;

    &:hover {
      border-bottom: 1px dashed #666;
      margin-bottom: -1px;
    }

    &.pro:hover {
      border-bottom: none;
      margin-bottom: 0;
      cursor: auto;
    }

    &.is-invalid {
      color: rgb(187, 59, 20);
    }
}

.player-limit {
  cursor: pointer;
  user-select: none;
  // border-radius: 4px;
  // border: 1px solid #ccc;
  // padding: 10px;

  .label {
    margin-bottom: 00px;
  }

  span {
    text-decoration: underline;
    text-decoration-thickness: .15em;
  }

  .title {
    font-size: 2.2em;
    // border: 1px solid #eee;
    // border-radius: 4px;
    // padding: 10px;
  }
}

.pro-options {
  .toggle {
    position: absolute;
    margin: 0 auto;
    text-align: center;
    top: -10px;
    left: 0; right: 0;
    width: fit-content;
    background-color: #eee;
    padding: 2px 5px;
    font-size: .7em;
    border-radius: 4px;
  }
}
</style>
